export interface KeycloakConfig {
    url: string;
    realm: string;
    clientId: string;
    clientSecret: string;
}

export interface KeycloakTokenResponse {
    access_token: string;
    refresh_token: string;
    id_token: string;
    expires_in: number;
    refresh_expires_in: number;
    token_type: string;
    scope: string;
}

export interface KeycloakUserInfo {
    sub: string;
    email?: string;
    name?: string;
    preferred_username?: string;
    given_name?: string;
    family_name?: string;
    email_verified?: boolean;
}

export interface KeycloakState {
    config: KeycloakConfig;
    accessToken: string | null;
    refreshToken: string | null;
    idToken: string | null;
    tokenExpiry: number | null;
    refreshTokenExpiry: number | null;
    authenticated: boolean;
    baseUrl: string;
    tokenRefreshTimeout: number | null;
    pkceCodeVerifier: string | null;
}

const createBaseUrl = (config: KeycloakConfig): string => {
    return `${config.url}/realms/${config.realm}/protocol/openid-connect`;
};

const generateRandomString = (): string => {
    const array = new Uint8Array(32);
    window.crypto.getRandomValues(array);
    return Array.from(array, (byte) => `0${(byte % 256).toString(16)}`.slice(-2)).join('');
};

const base64UrlEncode = (str: ArrayBuffer): string => {
    return btoa(String.fromCharCode.apply(null, new Uint8Array(str) as unknown as number[]))
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=+$/, '');
};

const generatePkceParameters = async (): Promise<{ codeVerifier: string; codeChallenge: string }> => {
    const codeVerifier = generateRandomString();

    const encoder = new TextEncoder();
    const data = encoder.encode(codeVerifier);
    const digest = await window.crypto.subtle.digest('SHA-256', data);
    const codeChallenge = base64UrlEncode(digest);

    return { codeVerifier, codeChallenge };
};

const saveTokens = (state: KeycloakState): void => {
    const tokens = {
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        idToken: state.idToken,
        tokenExpiry: state.tokenExpiry,
        refreshTokenExpiry: state.refreshTokenExpiry,
    };
    localStorage.setItem(`kc-tokens-${state.config.clientId}`, JSON.stringify(tokens));
};

const clearTokens = (state: KeycloakState): KeycloakState => {
    localStorage.removeItem(`kc-tokens-${state.config.clientId}`);

    if (state.tokenRefreshTimeout !== null) {
        window.clearTimeout(state.tokenRefreshTimeout);
    }

    return {
        ...state,
        accessToken: null,
        refreshToken: null,
        idToken: null,
        tokenExpiry: null,
        refreshTokenExpiry: null,
        authenticated: false,
        tokenRefreshTimeout: null,
    };
};

const restoreTokens = (state: KeycloakState): KeycloakState => {
    const storedTokens = localStorage.getItem(`kc-tokens-${state.config.clientId}`);
    if (!storedTokens) {
        return state;
    }

    try {
        const tokens = JSON.parse(storedTokens);
        const newState = {
            ...state,
            accessToken: tokens.accessToken,
            refreshToken: tokens.refreshToken,
            idToken: tokens.idToken,
            tokenExpiry: tokens.tokenExpiry,
            refreshTokenExpiry: tokens.refreshTokenExpiry,
            authenticated: true,
        };

        return scheduleTokenRefresh(newState);
    } catch (error) {
        console.error('Failed to restore tokens from localStorage', error);
        return clearTokens(state);
    }
};

const processTokenResponse = (state: KeycloakState, tokenData: KeycloakTokenResponse): KeycloakState => {
    const now = Math.floor(Date.now() / 1000);

    const newState = {
        ...state,
        accessToken: tokenData.access_token,
        refreshToken: tokenData.refresh_token,
        idToken: tokenData.id_token,
        tokenExpiry: now + tokenData.expires_in,
        refreshTokenExpiry: now + tokenData.refresh_expires_in,
        authenticated: true,
    };

    saveTokens(newState);

    return scheduleTokenRefresh(newState);
};

const scheduleTokenRefresh = (state: KeycloakState): KeycloakState => {
    if (state.tokenRefreshTimeout !== null) {
        window.clearTimeout(state.tokenRefreshTimeout);
    }

    if (!state.tokenExpiry) {
        return {
            ...state,
            tokenRefreshTimeout: null,
        };
    }

    const now = Math.floor(Date.now() / 1000);
    const timeToExpiry = state.tokenExpiry - now;

    if (timeToExpiry <= 0) {
        refreshToken(state).catch(console.error);
        return {
            ...state,
            tokenRefreshTimeout: null,
        };
    }

    const refreshTime = (timeToExpiry - 30) * 1000;

    const timeoutId = window.setTimeout(() => {
        refreshToken(state).catch(console.error);
    }, refreshTime);

    return {
        ...state,
        tokenRefreshTimeout: timeoutId,
    };
};

const refreshToken = async (state: KeycloakState): Promise<KeycloakState> => {
    if (!state.refreshToken) {
        return clearTokens(state);
    }

    try {
        const params = new URLSearchParams({
            grant_type: 'refresh_token',
            client_id: state.config.clientId,
            refresh_token: state.refreshToken,
            client_secret: state.config.clientSecret,
        });

        const response = await fetch(`${state.baseUrl}/token`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
            },
            body: params,
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            console.error('Failed to refresh token', errorData);
            return clearTokens(state);
        }

        const tokenData = await response.json();
        return processTokenResponse(state, tokenData);
    } catch (error) {
        console.error('Error refreshing token', error);
        return clearTokens(state);
    }
};

const fetchTokens = async (state: KeycloakState, code: string, codeVerifier: string): Promise<KeycloakState> => {
    const redirectUri = window.location.origin;

    const params = new URLSearchParams({
        grant_type: 'authorization_code',
        client_id: state.config.clientId,
        code,
        code_verifier: codeVerifier,
        redirect_uri: redirectUri,
        client_secret: state.config.clientSecret,
    });

    const response = await fetch(`${state.baseUrl}/token`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: params,
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
            errorData.error_description || `Failed to fetch tokens: ${response.status} ${response.statusText}`,
        );
    }

    const tokenData = await response.json();
    return processTokenResponse(state, tokenData);
};

const handleAuthorizationResponse = async (state: KeycloakState): Promise<KeycloakState> => {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');

    if (!code) {
        throw new Error('No authorization code found in URL');
    }

    const codeVerifier = sessionStorage.getItem(`kc-pkce-${state.config.clientId}`);
    if (!codeVerifier) {
        throw new Error('No code verifier found');
    }

    try {
        const newState = await fetchTokens(state, code, codeVerifier);
        window.history.replaceState({}, document.title, window.location.pathname);
        return newState;
    } finally {
        sessionStorage.removeItem(`kc-pkce-${state.config.clientId}`);
    }
};

export const createKeycloakService = (config: KeycloakConfig): KeycloakState => {
    const baseUrl = createBaseUrl(config);

    const initialState: KeycloakState = {
        config,
        accessToken: null,
        refreshToken: null,
        idToken: null,
        tokenExpiry: null,
        refreshTokenExpiry: null,
        authenticated: false,
        baseUrl,
        tokenRefreshTimeout: null,
        pkceCodeVerifier: null,
    };

    return restoreTokens(initialState);
};

export const isAuthenticated = (state: KeycloakState): boolean => {
    if (!state.authenticated || !state.accessToken || !state.tokenExpiry) {
        return false;
    }

    const now = Math.floor(Date.now() / 1000);
    return state.tokenExpiry > now;
};

export const initKeycloak = async (
    state: KeycloakState,
    options: { onLoad?: 'check-sso' | 'login-required'; silentCheckSsoRedirectUri?: string },
): Promise<{ state: KeycloakState; authenticated: boolean }> => {
    if (window.location.search.includes('code=')) {
        try {
            const newState = await handleAuthorizationResponse(state);
            return { state: newState, authenticated: isAuthenticated(newState) };
        } catch (error) {
            console.error('Failed to handle authorization response', error);
            return { state: clearTokens(state), authenticated: false };
        }
    }

    if (isAuthenticated(state)) {
        return { state, authenticated: true };
    }

    if (options.onLoad === 'login-required') {
        login(state);
        return { state, authenticated: false };
    }

    return { state, authenticated: false };
};

export const login = async (
    state: KeycloakState,
    options?: { redirectUri?: string; provider?: string },
): Promise<void> => {
    const redirectUri = options?.redirectUri || window.location.origin;

    const pkceParams = await generatePkceParameters();

    sessionStorage.setItem(`kc-pkce-${state.config.clientId}`, pkceParams.codeVerifier);

    const params = new URLSearchParams({
        client_id: state.config.clientId,
        redirect_uri: redirectUri,
        response_type: 'code',
        scope: 'openid',
        code_challenge: pkceParams.codeChallenge,
        code_challenge_method: 'S256',
    });

    if (options?.provider) {
        params.append('kc_idp_hint', options.provider);
    }

    window.location.href = `${state.baseUrl}/auth?${params.toString()}`;
};

export const logout = (state: KeycloakState, options?: { redirectUri?: string }): void => {
    const redirectUri = options?.redirectUri || window.location.origin;

    clearTokens(state);

    const params = new URLSearchParams({
        client_id: state.config.clientId,
        post_logout_redirect_uri: redirectUri,
    });

    if (state.idToken) {
        params.append('id_token_hint', state.idToken);
    }

    window.location.href = `${state.baseUrl}/logout?${params.toString()}`;
};

export const getUserInfo = async (state: KeycloakState): Promise<KeycloakUserInfo | null> => {
    if (!isAuthenticated(state)) {
        return null;
    }

    try {
        const response = await fetch(`${state.baseUrl}/userinfo`, {
            headers: {
                Authorization: `Bearer ${state.accessToken}`,
            },
        });

        if (!response.ok) {
            throw new Error(`Failed to fetch user info: ${response.status} ${response.statusText}`);
        }

        return await response.json();
    } catch (error) {
        console.error('Error fetching user info', error);
        return null;
    }
};

export const getToken = (state: KeycloakState): string | null => {
    return state.accessToken;
};

export const getTokenParsed = (state: KeycloakState): any | null => {
    if (!state.accessToken) return null;

    try {
        const base64Url = state.accessToken.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(
            atob(base64)
                .split('')
                .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
                .join(''),
        );

        return JSON.parse(jsonPayload);
    } catch (error) {
        console.error('Error parsing token', error);
        return null;
    }
};

export const updateToken = async (
    state: KeycloakState,
    minValidity: number = 10,
): Promise<{ state: KeycloakState; updated: boolean }> => {
    if (!isAuthenticated(state)) {
        return { state, updated: false };
    }

    const tokenExpiryTime = state.tokenExpiry || 0;
    const now = Math.floor(Date.now() / 1000);

    if (tokenExpiryTime - now < minValidity) {
        try {
            const newState = await refreshToken(state);
            return { state: newState, updated: true };
        } catch (error) {
            return { state, updated: false };
        }
    }

    return { state, updated: true };
};

export const loginWithCredentials = async (
    state: KeycloakState,
    username: string,
    password: string,
): Promise<KeycloakState> => {
    if (!username || !password) {
        throw new Error('Username and password are required');
    }

    try {
        const params = new URLSearchParams({
            client_id: state.config.clientId,
            grant_type: 'password',
            username,
            password,
            scope: 'openid',
            client_secret: state.config.clientSecret,
        });

        const response = await fetch(`${state.baseUrl}/token`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
            },
            body: params,
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.error_description || 'Login failed');
        }

        const tokenData = await response.json();
        const newState = processTokenResponse(state, tokenData);

        const userInfo = await getUserInfo(newState);
        if (!userInfo?.email_verified) {
            clearTokens(newState);
            throw new Error('Account is not fully set up. Please verify your email first.');
        }

        return newState;
    } catch (error) {
        console.error('Error logging in with credentials:', error);
        throw error;
    }
};

export const directRegister = async (state: KeycloakState, email: string, password: string): Promise<KeycloakState> => {
    try {
        const response = await fetch(`${state.config.url}/admin/realms/${state.config.realm}/users`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                username: email,
                email,
                enabled: true,
                emailVerified: false,
                credentials: [
                    {
                        type: 'password',
                        value: password,
                        temporary: false,
                    },
                ],
            }),
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.errorMessage || 'Registration failed');
        }

        return loginWithCredentials(state, email, password);
    } catch (error) {
        console.error('Registration failed:', error);
        throw error;
    }
};

export const checkEmailVerification = async (state: KeycloakState): Promise<boolean> => {
    try {
        const userInfo = await getUserInfo(state);
        return userInfo?.email_verified || false;
    } catch (error) {
        console.error('Failed to check email verification status:', error);
        return false;
    }
};

export const registerOrLogin = async (
    state: KeycloakState,
    email: string,
    password: string,
): Promise<{ state: KeycloakState; registered: boolean; authenticated: boolean }> => {
    if (!email || !password) {
        throw new Error('Email and password are required');
    }

    try {
        try {
            const params = new URLSearchParams({
                grant_type: 'password',
                client_id: state.config.clientId,
                username: email,
                password,
                scope: 'openid',
                client_secret: state.config.clientSecret,
            });

            const response = await fetch(`${state.baseUrl}/token`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
                body: params,
            });

            if (response.ok) {
                const tokenData = await response.json();
                const newState = processTokenResponse(state, tokenData);

                const userInfo = await getUserInfo(newState);
                if (!userInfo?.email_verified) {
                    clearTokens(newState);
                    throw new Error('Account is not fully set up. Please verify your email first.');
                }

                return {
                    state: newState,
                    registered: false,
                    authenticated: true,
                };
            }
        } catch (error) {
            console.log('Login failed, will try registration:', error);
        }

        const authServiceUrl = import.meta.env.VITE_AUTH_SERVICE_URL;
        const registrationResponse = await fetch(`${authServiceUrl}/api/auth/register-or-login`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ email, password }),
        });

        if (!registrationResponse.ok) {
            const errorData = await registrationResponse.json();
            throw new Error(errorData.detail || 'Registration failed');
        }

        const result = await registrationResponse.json();

        if (result.registered === true) {
            return {
                state,
                registered: true,
                authenticated: false,
            };
        }

        if (result.tokens) {
            const newState = processTokenResponse(state, result.tokens);
            return {
                state: newState,
                registered: false,
                authenticated: true,
            };
        }

        if (result.message && result.message.includes('Registration successful')) {
            return {
                state,
                registered: true,
                authenticated: false,
            };
        }

        throw new Error('Unexpected response from registration service');
    } catch (error) {
        console.error('Error in registerOrLogin:', error);
        throw error;
    }
};

export const keycloakReducer = (state: KeycloakState, action: any): KeycloakState => {
    switch (action.type) {
        case 'UPDATE_STATE':
            return { ...state, ...action.payload };
        case 'CLEAR_TOKENS':
            return clearTokens(state);
        default:
            return state;
    }
};
